import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useNavigate, useParams } from "react-router-dom";
import DeliveryPincodesDialog from "./DeliveryPincodesDialog";
import { EmptyAddCard } from "../../components/settings/EmptyAddCard";
import { getRoomsByFloorId, deleteRoom } from "../../services/WarehouseSettings";
import AddEditRoom from "../../components/settings/AddEditRoom";

const RoomsPage = () => {
  const navigate = useNavigate();
  const { buildingId, floorId } = useParams();
  const [rooms, setRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [pincodesDialogOpen, setPincodesDialogOpen] = useState(false);

  const loadRooms = useCallback(async () => {
    try {
      if (!floorId) return;
      setLoading(true);
      const data = await getRoomsByFloorId(Number(floorId));
      setRooms(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load failed:", err);
      setRooms([]);
      setSnackbar({ open: true, message: "Failed to load rooms", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [floorId]);

  useEffect(() => {
    if (floorId) {
      loadRooms();
    }
  }, [floorId, loadRooms]);

  const openAddRoom = () => {
    setEditData(null);
    setDialogOpen(true);
  };

  const openEditRoom = (room: any) => {
    setEditData(room);
    setDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSuccess = (message: string) => {
    setSnackbar({ open: true, message, severity: "success" });
    loadRooms();
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteRoom(id);
      loadRooms();
      setSnackbar({ open: true, message: "Room deleted successfully", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to delete room", severity: "error" });
    }
    setAnchorEl(null);
  };

  return (
    <Box sx={{ p: 2, height: "calc(100vh - 3.5rem)", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Button
          onClick={() => navigate(`/settings/warehouse/buildings/${buildingId}/floors`)}
          sx={{ border: "none", textTransform: "none", color: "text.primary" }}
          startIcon={<ArrowBackIosNewOutlinedIcon fontSize="small" />}
        >
          Back
        </Button>
        <Button
          variant="outlined"
          onClick={() => setPincodesDialogOpen(true)}
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            color: "primary.main",
            textTransform: "none",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "transparent",
              borderColor: "primary.main",
            },
            "&:active": {
              backgroundColor: "transparent",
            },
          }}
          endIcon={<SettingsOutlinedIcon sx={{ color: "primary.main" }} />}
        >
          Delivery Pin codes
        </Button>
      </Box>

      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexShrink: 0 }}>
          <Typography variant="h6">All Rooms - {rooms.length}</Typography>
          {rooms.length > 0 && (
            <Button
              variant="contained"
              endIcon={<AddIcon />}
              onClick={openAddRoom}
              sx={{ borderRadius: "6px", textTransform: "none" }}
            >
              Add Room
            </Button>
          )}
        </Box>

        <Box sx={{ 
          flex: 1, 
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px"
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: "#a8a8a8"
            }
          },
          "&::-webkit-scrollbar-button": {
            display: "none"
          }
        }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
              <CircularProgress />
            </Box>
          ) : rooms.length === 0 ? (
            <EmptyAddCard
              onAdd={openAddRoom}
              title="Add Room"
              description="Click here to add your first room"
            />
          ) : (
            <Grid container spacing={2}>
              {rooms.map((room) => (
              <Grid item xs={12} sm={6} md={4} key={room.id}>
                <Card sx={{ borderRadius: 3, height: 200, width: "100%" }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between">
                      <Box>
                        <Typography fontWeight={600}>{room.roomName}</Typography>
                        <Typography variant="caption">{room.roomType?.replace(/_/g, " ")}</Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setSelectedRoom(room);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    <Box mt={2}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Capacity</Typography>
                        <Typography fontWeight={600}>
                          {room.availablePercentage === 0.0 && room.usedPercentage >= 100 ? room.usedPercentage : room.availablePercentage}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={room.availablePercentage === 0.0 && room.usedPercentage >= 100 ? room.usedPercentage : room.availablePercentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: "#E5E7EB",
                          "& .MuiLinearProgress-bar": { backgroundColor: room.availablePercentage === 0.0 && room.usedPercentage >= 100 ? "error.main" : room.availablePercentage <= 30 ? "warning.main" : "success.main" },
                        }}
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box display="flex" justifyContent="space-between">
                      <Box display="flex" alignItems="center">
                        <FiberManualRecordIcon sx={{ fontSize: 14, color: "#09B11B", mr: 0.5 }} />
                        <Typography>Available</Typography>
                      </Box>
                      <Typography>{room.numberOfRacks || 0} Items</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          )}
        </Box>
      </Paper>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => openEditRoom(selectedRoom)} sx={{ color: "primary.main" }}>
          <Box component="img" src="/edit.svg" alt="Edit" sx={{ width: "1.2rem", height: "1.2rem", mr: 1 }} />
          Edit
        </MenuItem>
        {selectedRoom?.roomType !== "Temporary_Storage" && (
          <MenuItem
            onClick={() =>
              navigate(`/settings/warehouse/buildings/${buildingId}/floors/${floorId}/rooms/${selectedRoom?.id}/racks`)
            }
          >
            <VisibilityOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} /> View
          </MenuItem>
        )}
        {selectedRoom?.canDelete && (
          <MenuItem sx={{ color: "red" }} onClick={() => handleDelete(selectedRoom?.id)}>
            <Box component="img" src="/delete.svg" alt="Delete" sx={{ width: "1.2rem", height: "1.2rem", mr: 0.5 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      <AddEditRoom
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editData={editData}
        buildingId={buildingId!}
        floorId={floorId!}
        onSuccess={handleSuccess}
      />

      <DeliveryPincodesDialog
        open={pincodesDialogOpen}
        onClose={() => setPincodesDialogOpen(false)}
        onSave={(pincodes) => {
          console.log("Saved pincodes:", pincodes);
          setSnackbar({ open: true, message: "Delivery pincodes saved successfully", severity: "success" });
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: "100%",
            backgroundColor: `${snackbar.severity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default RoomsPage;
